<script lang="ts">
  import { onDestroy, onMount } from 'svelte';
  import { get } from 'svelte/store';
  import type { AppContext, MissionInfo, TimelineEvent } from '../app';
  import {
    DAY,
    MAX_SPAN,
    type TickUnit,
    type TimelineView,
    YEAR,
    decayVelocity,
    snap,
    tickSpacing,
    ticks as makeTicks,
    timeToX,
    zoomSpan,
  } from '../core/timelineScale';
  import { formatUtc } from '../core/time';
  import { isShown } from '../core/missionState';
  import { DESTINATIONS, type LaneItem, destinationOf, packLanes } from '../core/timelineLanes';
  import { lang, t, type MessageKey } from '../i18n';
  import { laneGrouping, motionReduced } from '../state/settings';
  import { currentDate, focusOn, selectedMission, selectMission, setDate, timeRange } from '../state/stores';
  import Legend from './Legend.svelte';
  import TimelineBar from './TimelineBar.svelte';
  import { yieldToMain } from '../scene/Scene';

  // The mission timeline (S3-15, D14), replacing the Sprint 2 scrubber: a fixed playhead in the middle, the track
  // scrolls under it like a tape. Drag (with inertia), wheel, pinch, keys and buttons zoom from 70 years to one day.
  // Missions share lanes when they do not overlap (core/timelineLanes.ts), grouped by agency or destination if
  // chosen; on phones the lanes collapse into one combined row, and the zoom row hides, until the dock is expanded
  // (its key sits in the dock's first row, S4-15).
  let { ctx, expanded = false }: { ctx: AppContext; expanded?: boolean } = $props();

  let width = $state(320);
  /**
   * The mission lanes mount one task after the rest of the timeline, inside their already sized box (S4-03 part 6):
   * together with the dock they were one 150 ms task at 4× CPU (Lighthouse counts every task above 50 ms).
   */
  let lanesShown = $state(false);
  onMount(() => {
    void yieldToMain().then(() => (lanesShown = true));
  });
  let span = $state(56 * YEAR);
  let area: HTMLDivElement;

  /**
   * The strip moves only when it would move by half a pixel (S4-15): every tick, label and bar is laid out again on
   * each change, and playback changes the date every frame — at 1 month/s over 56 years the strip now moves every
   * 1.7 s instead of 60 times a second; zoomed in, it still moves smoothly.
   */
  const center = $derived.by(() => {
    const step = span / Math.max(1, width) / 2;
    return Math.round($currentDate / step) * step;
  });
  const view = $derived<TimelineView>({ center, span, width });
  /** The slider's value for assistive technology: the day (its text has day precision). */
  const day = $derived(Math.floor($currentDate / DAY) * DAY);
  let listEl: HTMLDivElement;

  // ── lanes ───────────────────────────────────────────────────────────────

  /** Estimated label width (spaced capitals at 0.7rem, icon and padding), px; the packing reserves it. */
  const LABEL_CHAR_PX = 8.4;
  const LABEL_PAD_PX = 30;
  /** Free space between two missions in a lane, px. */
  const LANE_GAP_PX = 10;
  const labelWidth = (m: MissionInfo, l: 'en' | 'de'): number => LABEL_PAD_PX + m.data.name[l].length * LABEL_CHAR_PX;
  /** Lanes are packed for the span rounded up to a power of two: they stay put while zooming within it. */
  const packSpan = $derived(2 ** Math.ceil(Math.log2(span)));

  function groupOf(m: MissionInfo): string {
    if ($laneGrouping === 'agency') return m.agencies[0]?.id ?? '';
    if ($laneGrouping === 'destination') {
      return destinationOf(
        m.data.targets,
        (id) => ctx.targets.get(id)?.parent ?? null,
        (id) => ctx.targets.get(id)?.bodyKind ?? null,
      );
    }
    return '';
  }

  const groups = $derived.by(() => {
    const msPerPx = packSpan / Math.max(width, 1);
    const max = $timeRange.max;
    const items: LaneItem[] = ctx.missions.map((m) => {
      // the bar's extent as TimelineBar draws it: solid, silent and predicted parts
      const trajEnd = Math.min(m.trajectoryEnd ?? -Infinity, max);
      let end = Math.min(m.end ?? Infinity, ctx.now);
      if (m.end !== null && trajEnd > m.end) end = Math.max(end, trajEnd);
      if (m.end === null) end = Math.max(end, trajEnd, ctx.now);
      const dot = end - m.launch < 6 * msPerPx;
      const label = (labelWidth(m, $lang) + (dot ? 12 : 0)) * msPerPx;
      return { id: m.id, start: m.launch, end: Math.max(end, m.launch + label), group: groupOf(m) };
    });
    const order =
      $laneGrouping === 'destination'
        ? [...DESTINATIONS]
        : [...new Set(ctx.missions.flatMap((m) => m.agencies.map((a) => a.id)))];
    return packLanes(items, LANE_GAP_PX * msPerPx, order);
  });
  const laneCount = $derived(groups.reduce((n, g) => n + g.lanes.length, 0));
  /**
   * Phones, collapsed: all lanes as thin lines in one row of fixed maximum height (S4-10: a seventh lane pushed the
   * HUD over its 35 % budget; P2 brings ~100 missions) — lines 2.5 px apart while they fit, then closer.
   */
  const COMBINED_MAX = 18;
  const combinedStep = $derived(Math.min(2.5, (COMBINED_MAX - 3) / Math.max(1, laneCount)));
  const combinedHeight = $derived(Math.max(12, Math.min(COMBINED_MAX, 3 + laneCount * 2.5)));

  function groupName(key: string, l: 'en' | 'de'): string {
    if ($laneGrouping === 'destination') return $t(`timeline.dest.${key}` as MessageKey);
    for (const m of ctx.missions) for (const a of m.agencies) if (a.id === key) return a.name[l];
    return key;
  }

  /** Lane height and group heading height, px (with their 1 px / 2 px rules). */
  const LANE_H = 27;
  const GROUP_H = 19;

  /**
   * The lane layout: every mission's lane index (all groups counted) and its top in the list; group headings and lane
   * rules. All bars are one flat keyed list positioned by lane, so a mission that changes its lane (zoom, a narrower
   * timeline next to the panel) keeps its button — and the keyboard focus.
   */
  const layout = $derived.by(() => {
    const lane: Record<string, number> = {};
    const top: Record<string, number> = {};
    const headings: { key: string; y: number }[] = [];
    const rules: number[] = [];
    let y = 0;
    let i = 0;
    for (const g of groups) {
      if ($laneGrouping !== 'none') {
        headings.push({ key: g.key, y });
        y += GROUP_H;
      }
      for (const ids of g.lanes) {
        rules.push(y);
        for (const id of ids) {
          lane[id] = i;
          top[id] = y;
        }
        y += LANE_H;
        i++;
      }
    }
    return { lane, top, headings, rules, height: y };
  });

  function cycleGrouping(): void {
    laneGrouping.update((g) => (g === 'none' ? 'agency' : g === 'agency' ? 'destination' : 'none'));
  }

  // the selected mission's lane scrolls into view (a selection in the scene or the list)
  $effect(() => {
    const id = $selectedMission;
    if (!id || !listEl) return;
    const top = layout.top[id];
    if (top === undefined) return;
    if (top < listEl.scrollTop) listEl.scrollTop = top;
    else if (top + LANE_H > listEl.scrollTop + listEl.clientHeight)
      listEl.scrollTop = top + LANE_H - listEl.clientHeight;
  });
  const tickList = $derived(makeTicks(view, 14));
  const selected = $derived(ctx.missions.find((m) => m.id === $selectedMission) ?? null);
  /** Event ticks of the selected mission (magnetic snap, tap = jump). */
  const eventTicks = $derived(selected ? selected.events : []);
  const eventTimes = $derived(eventTicks.map((e) => e.time));

  // ── labels ──────────────────────────────────────────────────────────────

  // formatters are built once per language and pattern (constructing one per label and frame cost a phone's frame)
  // eslint-disable-next-line svelte/prefer-svelte-reactivity
  const formatters = new Map<string, Intl.DateTimeFormat>();
  const fmt = (opts: Intl.DateTimeFormatOptions, l: string, ms: number): string => {
    const key = `${l}:${JSON.stringify(opts)}`;
    let f = formatters.get(key);
    if (!f)
      formatters.set(key, (f = new Intl.DateTimeFormat(l === 'de' ? 'de-DE' : 'en-GB', { ...opts, timeZone: 'UTC' })));
    return f.format(ms);
  };

  function tickLabel(time: number, unit: TickUnit, l: string): string {
    const d = new Date(time);
    if (unit === 'decade' || unit === 'year') return String(d.getUTCFullYear());
    if (unit === 'month') return d.getUTCMonth() === 0 ? String(d.getUTCFullYear()) : fmt({ month: 'short' }, l, time);
    if (unit === 'day') return d.getUTCDate() === 1 ? fmt({ month: 'short' }, l, time) : String(d.getUTCDate());
    return d.getUTCHours() === 0 ? fmt({ day: 'numeric', month: 'short' }, l, time) : `${d.getUTCHours()}:00`;
  }

  /** Which labels fit: one every ~44 px (labels are wider than ticks). */
  const labelled = $derived.by(() => {
    // a plain set inside a derived value: rebuilt on every change, never mutated reactively
    // eslint-disable-next-line svelte/prefer-svelte-reactivity
    const out = new Set<number>();
    let lastX = -Infinity;
    for (const tick of tickList) {
      if (!tick.major) continue;
      const x = timeToX(view, tick.time);
      if (x - lastX >= 44) {
        out.add(tick.time);
        lastX = x;
      }
    }
    return out;
  });

  function missionName(m: MissionInfo, l: 'en' | 'de'): string {
    const from = new Date(m.launch).getUTCFullYear();
    const to = m.end !== null ? String(new Date(m.end - 1).getUTCFullYear()) : $t('timeline.today');
    return `${m.data.name[l]}, ${from}–${to}`;
  }

  // ── zoom ────────────────────────────────────────────────────────────────

  function zoom(steps: number): void {
    span = zoomSpan(span, steps);
  }

  // ── dragging, pinch, inertia ─────────────────────────────────────────────

  // gesture bookkeeping, deliberately not reactive
  // eslint-disable-next-line svelte/prefer-svelte-reactivity
  const pointers = new Map<number, { x: number; y: number }>();
  let drag: { x: number; center: number; moved: boolean; samples: { t: number; x: number }[] } | null = null;
  let pinch: { dist: number; span: number } | null = null;
  /** Set right after a drag, so the click that ends it does not select a row. */
  let suppressClick = false;
  let velocity = 0;
  let raf = 0;
  const SLOP = 6;

  function clampToRange(ms: number): number {
    const r = get(timeRange);
    return Math.min(r.max, Math.max(r.min, ms));
  }

  function onPointerDown(e: PointerEvent): void {
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    stopInertia();
    if (pointers.size === 1) {
      drag = { x: e.clientX, center: get(currentDate), moved: false, samples: [{ t: e.timeStamp, x: e.clientX }] };
    } else if (pointers.size === 2) {
      const [a, b] = [...pointers.values()];
      pinch = { dist: Math.max(Math.abs(a!.x - b!.x), 1), span };
      drag = null;
    }
  }

  function onPointerMove(e: PointerEvent): void {
    if (!pointers.has(e.pointerId)) return;
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pinch && pointers.size === 2) {
      const [a, b] = [...pointers.values()];
      span = zoomSpan(pinch.span * (pinch.dist / Math.max(Math.abs(a!.x - b!.x), 1)), 0);
      return;
    }
    if (!drag) return;
    const dx = e.clientX - drag.x;
    if (!drag.moved && Math.abs(dx) < SLOP) return;
    if (!drag.moved) {
      drag.moved = true;
      area.setPointerCapture(e.pointerId);
    }
    setDate(clampToRange(drag.center - dx * (span / width)));
    drag.samples.push({ t: e.timeStamp, x: e.clientX });
    if (drag.samples.length > 6) drag.samples.shift();
  }

  function onPointerUp(e: PointerEvent): void {
    pointers.delete(e.pointerId);
    if (pointers.size < 2) pinch = null;
    if (!drag) return;
    const d = drag;
    drag = null;
    if (!d.moved) return;
    suppressClick = true;
    setTimeout(() => (suppressClick = false), 0);
    if (area.hasPointerCapture(e.pointerId)) area.releasePointerCapture(e.pointerId);
    // fling: the last samples' speed carries on and eases out (no inertia with reduced motion)
    const first = d.samples[0]!;
    const last = d.samples[d.samples.length - 1]!;
    const dt = (last.t - first.t) / 1000;
    // a fling needs a real gesture: samples over at least 30 ms, the last one shortly before the release
    const recent = e.timeStamp - last.t < 80;
    const pxPerS = dt >= 0.03 && recent ? (last.x - first.x) / dt : 0;
    if (!get(motionReduced) && Math.abs(pxPerS) > 150) {
      velocity = -pxPerS * (span / width);
      startInertia();
    } else {
      snapNow();
    }
  }

  function snapNow(): void {
    if (!eventTimes.length) return;
    setDate(snap(view, get(currentDate), eventTimes));
  }

  let lastFrame = 0;
  function startInertia(): void {
    lastFrame = performance.now();
    const step = (now: number): void => {
      const dt = Math.min((now - lastFrame) / 1000, 0.05);
      lastFrame = now;
      const next = clampToRange(get(currentDate) + velocity * dt);
      setDate(next);
      velocity = decayVelocity(velocity, dt, 0.35, (span / width) * 10);
      const r = get(timeRange);
      if (velocity === 0 || next <= r.min || next >= r.max) {
        raf = 0;
        snapNow();
        return;
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
  }

  function stopInertia(): void {
    if (raf) cancelAnimationFrame(raf);
    raf = 0;
    velocity = 0;
  }
  onDestroy(stopInertia);

  // wheel: vertical zooms around the playhead, horizontal scrolls (non-passive: the page must not scroll)
  $effect(() => {
    const el = area;
    const onWheel = (e: WheelEvent): void => {
      e.preventDefault();
      stopInertia();
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
        setDate(clampToRange(get(currentDate) + e.deltaX * (span / width)));
      } else {
        span = zoomSpan(span, -e.deltaY / 200);
      }
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  });

  // ── keyboard (the track is an ARIA slider) ───────────────────────────────

  const UNIT_STEP: Record<TickUnit, number> = {
    decade: 10 * YEAR,
    year: YEAR,
    month: YEAR / 12,
    day: DAY,
    hour: DAY / 24,
  };

  function onKeydown(e: KeyboardEvent): void {
    const [unit, step] = tickSpacing(view, 14);
    const stepMs = UNIT_STEP[unit] * step;
    const r = get(timeRange);
    switch (e.key) {
      case 'ArrowLeft':
      case 'ArrowDown':
        setDate(clampToRange(get(currentDate) - stepMs));
        break;
      case 'ArrowRight':
      case 'ArrowUp':
        setDate(clampToRange(get(currentDate) + stepMs));
        break;
      case 'Home':
        setDate(r.min);
        break;
      case 'End':
        setDate(r.max);
        break;
      case 'PageUp':
      case '+':
      case '=':
        zoom(1);
        break;
      case 'PageDown':
      case '-':
        zoom(-1);
        break;
      default:
        return;
    }
    // the global shortcuts (←/→ = one day) must not fire as well
    e.preventDefault();
    e.stopPropagation();
  }

  // ── selection ────────────────────────────────────────────────────────────

  /** A tap on a row selects the mission; the camera flies to its spacecraft if it is in flight now (D16). */
  function select(id: string): void {
    if (suppressClick) return;
    const m = ctx.missions.find((x) => x.id === id);
    selectMission(id);
    if (!m) return;
    const now = get(currentDate);
    const flying = m.spacecraft.find((sc) => isShown(ctx.scene.lifeOf(sc)?.state));
    if (flying && now >= m.launch) focusOn(flying);
  }

  function jumpTo(e: TimelineEvent): void {
    if (suppressClick) return;
    setDate(e.time);
    if (e.spacecraft) focusOn(e.spacecraft);
  }

  const visibleSpan = $derived(
    span >= 2 * YEAR
      ? $t('timeline.spanYears', { n: String(Math.round(span / YEAR)) })
      : span >= 60 * DAY
        ? $t('timeline.spanMonths', { n: String(Math.round(span / (YEAR / 12))) })
        : $t('timeline.spanDays', { n: String(Math.max(1, Math.round(span / DAY))) }),
  );
</script>

<section class="timeline" class:expanded aria-label={$t('timeline.label')} data-testid="timeline">
  <!-- dragging anywhere on the timeline scrolls it; its controls (slider, rows, events) are the real buttons -->
  <div
    class="area"
    role="group"
    aria-label={$t('timeline.label')}
    bind:this={area}
    bind:clientWidth={width}
    onpointerdown={onPointerDown}
    onpointermove={onPointerMove}
    onpointerup={onPointerUp}
    onpointercancel={onPointerUp}
  >
    <!-- the track: ticks, labels, event ticks of the selected mission and the fixed playhead -->
    <div
      class="track"
      role="slider"
      tabindex="0"
      aria-label={$t('time.slider')}
      aria-valuemin={$timeRange.min}
      aria-valuemax={$timeRange.max}
      aria-valuenow={day}
      aria-valuetext={formatUtc(day, $lang, 'day')}
      aria-describedby="timeline-help"
      data-testid="timeline-track"
      onkeydown={onKeydown}
    >
      {#each tickList as tick (tick.time)}
        <span class="tick" class:major={tick.major} style:transform="translateX({timeToX(view, tick.time)}px)"></span>
        {#if labelled.has(tick.time)}
          <span class="tick-label" style:transform="translateX({timeToX(view, tick.time)}px)"
            >{tickLabel(tick.time, tick.unit, $lang)}</span
          >
        {/if}
      {/each}
      <span class="now-mark" style:transform="translateX({timeToX(view, ctx.now)}px)" aria-hidden="true"></span>
    </div>
    {#if eventTicks.length}
      <div class="events">
        {#each eventTicks as e (e.time + e.type)}
          <button
            type="button"
            class="event"
            style:transform="translateX({timeToX(view, e.time)}px)"
            aria-label="{e.title[$lang]}, {formatUtc(e.time, $lang, 'day')}"
            title={e.title[$lang]}
            onclick={() => jumpTo(e)}><span class="diamond" aria-hidden="true"></span></button
          >
        {/each}
      </div>
    {/if}
    <div class="rows" class:expanded>
      <!-- phones, collapsed: every lane as a thin line in one combined row -->
      <div class="combined" aria-hidden="true" style:height="{combinedHeight}px">
        {#each ctx.missions as m (m.id)}
          <span
            class="thin"
            style:--bar-color={m.color}
            style:top="{1.5 + (layout.lane[m.id] ?? 0) * combinedStep}px"
            style:transform="translateX({Math.max(-20, timeToX(view, m.launch))}px)"
            style:width="{Math.max(
              2,
              Math.min(width + 20, timeToX(view, m.end ?? m.trajectoryEnd ?? ctx.now)) -
                Math.max(-20, timeToX(view, m.launch)),
            )}px"
          ></span>
        {/each}
      </div>
      <div class="list" role="group" aria-label={$t('timeline.missions')} bind:this={listEl}>
        <div class="lanes" style:height="{layout.height}px">
          {#each layout.rules as y (y)}
            <span class="lane-rule" style:top="{y}px" aria-hidden="true"></span>
          {/each}
          {#each layout.headings as h (h.key)}
            <div class="group-label" style:top="{h.y}px" aria-hidden="true">{groupName(h.key, $lang)}</div>
          {/each}
          {#each lanesShown ? ctx.missions : [] as m (m.id)}
            <div class="lane" style:top="{layout.top[m.id] ?? 0}px">
              <TimelineBar
                mission={m}
                {view}
                now={ctx.now}
                max={$timeRange.max}
                selected={m.id === $selectedMission}
                onselect={select}
                name={missionName(m, $lang)}
                labelWidth={labelWidth(m, $lang)}
              />
            </div>
          {/each}
        </div>
      </div>
    </div>
    <div class="playhead" aria-hidden="true"></div>
  </div>
  <div class="controls">
    <span class="span-label" aria-live="off">{visibleSpan}</span>
    <Legend {ctx} />
    <button
      type="button"
      class="key"
      aria-label={$t('timeline.zoomOut')}
      title="{$t('timeline.zoomOut')} (−)"
      onclick={() => zoom(-1)}
      disabled={span >= MAX_SPAN}>−</button
    >
    <button
      type="button"
      class="key"
      aria-label={$t('timeline.zoomIn')}
      title="{$t('timeline.zoomIn')} (+)"
      onclick={() => zoom(1)}
      disabled={span <= DAY}>+</button
    >
    <button
      type="button"
      class="key wide group"
      aria-label={$t('timeline.groupLabel', { mode: $t(`timeline.group.${$laneGrouping}` as MessageKey) })}
      title={$t('timeline.groupLabel', { mode: $t(`timeline.group.${$laneGrouping}` as MessageKey) })}
      onclick={cycleGrouping}>{$t(`timeline.groupShort.${$laneGrouping}` as MessageKey)}</button
    >
    <button type="button" class="key wide" onclick={() => setDate(Math.min(ctx.now, $timeRange.max))}
      >{$t('jump.today')}</button
    >
  </div>
  <p id="timeline-help" class="visually-hidden">{$t('timeline.help')}</p>
</section>

<style>
  .timeline {
    display: flex;
    flex-direction: column;
    gap: 4px;
    user-select: none;
    -webkit-user-select: none;
  }
  .area {
    position: relative;
    overflow: hidden;
    touch-action: none;
    cursor: grab;
    border: var(--rule-w) solid var(--rule);
    background: var(--ink);
  }
  .track {
    position: relative;
    height: 26px;
    outline-offset: -2px;
  }
  .tick {
    position: absolute;
    left: 0;
    bottom: 0;
    width: 1px;
    height: 5px;
    background: var(--rule);
  }
  .tick.major {
    height: 9px;
    background: var(--fg-muted);
  }
  .tick-label {
    position: absolute;
    left: 0;
    top: 2px;
    margin-left: 3px;
    font-family: var(--font-mono);
    font-size: 0.65rem;
    color: var(--fg-muted);
    white-space: nowrap;
  }
  .now-mark {
    position: absolute;
    left: -3px;
    bottom: 0;
    width: 6px;
    height: 6px;
    background: var(--cream);
    clip-path: polygon(50% 0, 100% 100%, 0 100%);
  }
  .events {
    position: relative;
    height: 16px;
  }
  .event {
    position: absolute;
    left: -8px;
    top: 0;
    width: 16px;
    height: 16px;
    padding: 0;
    display: grid;
    place-items: center;
  }
  /* 44 px touch targets */
  .event::after {
    content: '';
    position: absolute;
    inset: -14px;
  }
  .diamond {
    width: 8px;
    height: 8px;
    background: var(--signal);
    transform: rotate(45deg);
  }
  .event:hover .diamond,
  .event:focus-visible .diamond {
    background: var(--accent);
  }
  .rows {
    position: relative;
  }
  .combined {
    position: relative;
    max-height: 60px;
    overflow: hidden;
    border-top: 1px solid var(--rule);
  }
  .lanes {
    position: relative;
  }
  /* a mission's slot: as tall as its lane, as wide as the track; only its bar button takes pointer events */
  .lane {
    position: absolute;
    left: 0;
    right: 0;
    height: 26px;
    margin-top: 1px;
    pointer-events: none;
  }
  .lane :global(.bar-button) {
    pointer-events: auto;
  }
  .lane-rule {
    position: absolute;
    left: 0;
    right: 0;
    height: 1px;
    background: var(--rule);
  }
  .group-label {
    position: absolute;
    left: 0;
    right: 0;
    height: 17px;
    padding: 3px 6px 0;
    border-top: var(--rule-w) solid var(--rule);
    box-sizing: border-box;
    color: var(--fg-muted);
    font-size: 0.6rem;
    font-weight: 700;
    letter-spacing: var(--track-label);
    text-transform: uppercase;
  }
  .thin {
    position: absolute;
    left: 0;
    height: 2px;
    background: var(--bar-color);
  }
  .list {
    display: none;
    max-height: 40vh;
    overflow-y: auto;
  }
  .rows.expanded .combined {
    display: none;
  }
  .rows.expanded .list {
    display: block;
  }
  .playhead {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 50%;
    width: 2px;
    margin-left: -1px;
    background: var(--accent);
    pointer-events: none;
  }
  .controls {
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .span-label {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    white-space: nowrap;
    font-family: var(--font-mono);
    font-size: 0.7rem;
    color: var(--fg-muted);
  }
  .key {
    position: relative;
    min-width: 36px;
    min-height: 32px;
    padding: 0 10px;
    border: var(--rule-w) solid var(--rule);
    font-family: var(--font-mono);
    font-weight: 700;
  }
  .key::after {
    content: '';
    position: absolute;
    inset: -6px -2px;
  }
  .key.wide {
    font-family: var(--font-ui);
    font-size: 0.72rem;
    letter-spacing: var(--track-caps);
    text-transform: uppercase;
  }
  .key:disabled {
    opacity: 0.4;
    cursor: default;
  }
  .key:not(:disabled):hover {
    border-color: var(--accent);
    color: var(--accent);
  }
  /* phones (S4-15): the zoom row, the lanes and the event ticks open with the dock's expand key (the chronology in
     the panel keeps the events); a lower track */
  @media (max-width: 899px) {
    .timeline:not(.expanded) .controls,
    .timeline:not(.expanded) .events {
      display: none;
    }
    .track {
      height: 22px;
    }
  }
  /* desktop: the rows are always shown (D14) */
  @media (min-width: 900px) {
    .combined {
      display: none;
    }
    .list {
      display: block;
    }
  }
</style>
